import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RiUserLine, RiPlaneLine, RiTeamLine, RiArrowRightSLine, RiCheckboxCircleLine, RiTimeLine, RiCloseCircleLine, RiBuildingLine, RiFileTextLine, RiArticleLine } from '@remixicon/react';
import { Card, CardHeader, CardContent, CardFooter } from '@components/ui/Card';
import { Avatar, AvatarFallback, AvatarImage } from '@components/ui/Avatar';
import { Badge } from '@components/ui/Badge';
import Button from '@components/ui/Button';
import FlightLeg from './FlightLeg';

/**
 * BidCard Component
 * Displays a single bid with client info, flight details, and actions
 *
 * @param {Object} props
 * @param {Object} props.bid - Bid data object
 * @param {Function} props.onViewBid - Callback when "View Quote" is clicked
 */
const BidCard = ({ bid, onViewBid }) => {
  const navigate = useNavigate();

  const handleViewClick = () => {
    if (onViewBid) {
      onViewBid(bid);
    } else {
      navigate(`/bids/${bid.id}`);
    }
  };

  // Get status badge variant and icon
  const getStatusConfig = () => {
    switch (bid.status) {
      case 'pending':
        return { variant: 'default', icon: RiTimeLine, color: 'bg-yellow-100 text-yellow-700 border-yellow-200' };
      case 'accepted':
        return { variant: 'default', icon: RiCheckboxCircleLine, color: 'bg-green-100 text-green-700 border-green-200' };
      case 'rejected':
        return { variant: 'destructive', icon: RiCloseCircleLine, color: 'bg-red-100 text-red-700 border-red-200' };
      case 'expired':
        return { variant: 'outline', icon: RiTimeLine, color: 'bg-gray-100 text-gray-700 border-gray-200' };
      default:
        return { variant: 'default', icon: RiTimeLine, color: 'bg-yellow-100 text-yellow-700 border-yellow-200' };
    }
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  return (
    <Card className="group hover:shadow-lg transition-all duration-200 overflow-hidden bg-white rounded-2xl">
      {/* Client Info Section */}
      <CardHeader className="p-5 pb-4 !border-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="relative shrink-0">
              <Avatar className="w-12 h-12 rounded-full">
                {bid.client?.avatarUrl ? (
                  <AvatarImage src={bid.client.avatarUrl} alt={bid.client.name} />
                ) : (
                  <AvatarFallback className="bg-gray-200 text-gray-700 font-medium text-sm">
                    {bid.client?.name?.charAt(0) || '?'}
                  </AvatarFallback>
                )}
              </Avatar>
              {bid.client?.isVerified && (
                <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center border-2 border-white">
                  <RiCheckboxCircleLine className="w-2.5 h-2.5 text-white" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 text-sm truncate">
                {bid.client?.name || 'Unknown Client'}
              </h3>
              <p className="text-xs text-gray-400 truncate flex items-center gap-1">
                <RiBuildingLine className="w-3 h-3 text-gray-400" />
                <span>{bid.client?.company || 'No company'}</span>
              </p>
            </div>
          </div>

          <button className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-50 transition-colors">
            <RiArrowRightSLine className="w-5 h-5 text-gray-300" />
          </button>
        </div>
      </CardHeader>

      {/* Flight Legs Section */}
      <CardContent className="px-5 pt-0 pb-4 space-y-2.5 h-[140px] flex flex-col">
        {bid.flights && bid.flights.length > 0 ? (
          <div className="space-y-0 flex-1 border-t border-gray-200 pt-4">
            {bid.flights.map((flight, index) => (
              <FlightLeg
                key={flight.id || index}
                origin={flight.origin}
                destination={flight.destination}
                duration={flight.duration}
                date={flight.date}
                isReturn={index > 0}
                isLast={index === bid.flights.length - 1}
              />
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center py-6 text-gray-400">
            <RiPlaneLine className="w-5 h-5 mr-2" />
            <p className="text-sm">No flight information available</p>
          </div>
        )}

        {/* Additional Info Section */}
        <div className="flex items-center gap-3 pt-2.5">
          <div className="flex items-center gap-1.5 text-gray-400">
            <RiTeamLine className="w-3.5 h-3.5" />
            <span className="text-xs">
              {bid.passengers || 0} passenger{bid.passengers !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </CardContent>

      {/* Status and Action Section */}
      <CardFooter className="px-5 pt-0 pb-5 flex-col gap-3 !border-0">
        
        {/*<div className="w-full flex items-center gap-2 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1.5">
            <RiFileTextLine className="w-3.5 h-3.5 text-gray-400" />
            <span>{bid.statusText}</span>
          </span>
          {bid.statusCount > 0 && (
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-md text-xs font-medium">
              {bid.statusCount}
            </span>
          )}
        </div>*/}
        
        <button
          onClick={handleViewClick}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-sm font-semibold rounded-xl transition-colors"
        >
          <RiArticleLine className="w-4 h-4" />
          <span>View Quote</span>
        </button>
      </CardFooter>
    </Card>
  );
};

export default BidCard;
